import * as React from 'react';
import { styled } from '@linaria/react';

import Demo, { DemoSliderControl } from '@/components/Demo';
import SvgWithGrid from '@/components/SvgWithGrid';
import { roundTo } from '@/utils';

import ArcSizeLine from './ArcSizeLine';
import { ARC_COLOR } from './ArcConstants';
import { calculateEllipseCenter } from './Paths.helpers';

interface Props {
  variant?: 'circle' | 'ellipse';
}

const P1 = { x: 10, y: 24 };
const P2 = { x: 22, y: 24 };

function ArcRadiusDemo({ variant = 'ellipse' }: Props) {
  const id = React.useId().replace(/:/g, '');
  const maskId = `${id}-mask`;

  let controls;

  if (variant === 'circle') {
    controls = [
      <DemoSliderControl
        highlightControlWhenActive
        key="rx"
        id="rx"
        label="Radius"
        min={6}
        max={16}
        step={0.02}
        previewRenderer={(val) => (
          // We slice `val` in half because I want to pretend that the viewBox is 16x16, to be consistent with other SVGs in this part of the course:
          <Value>{roundTo(Number(val) / 2, 2).toFixed(2)}</Value>
        )}
      />,
    ];
  } else {
    controls = [
      <DemoSliderControl
        highlightControlWhenActive
        key="rx"
        id="rx"
        label="rx"
        min={6}
        max={16}
        step={0.02}
        previewRenderer={(val) => (
          // We slice `val` in half because I want to pretend that the viewBox is 16x16, to be consistent with other SVGs in this part of the course:
          <Value>{roundTo(Number(val) / 2, 2).toFixed(2)}</Value>
        )}
      />,
      <DemoSliderControl
        highlightControlWhenActive
        key="ry"
        id="ry"
        label="ry"
        min={6}
        max={16}
        step={0.01}
        previewRenderer={(val) => (
          // We slice `val` in half because I want to pretend that the viewBox is 16x16, to be consistent with other SVGs in this part of the course:
          <Value>{roundTo(Number(val) / 2, 2).toFixed(2)}</Value>
        )}
      />,
    ];
  }

  return (
    <Demo
      outputWrapper={null}
      initialValues={{
        rx: variant === 'circle' ? 8 : 7,
        ry: variant === 'circle' ? 8 : 9,
      }}
      controls={controls}
      customGrid={
        variant === 'circle'
          ? {
              gridTemplateColumns: 'min(400px, 100%)',
              justifyContent: 'center',
            }
          : undefined
      }
    >
      {({ rx, ry: baseRy }, activeControlId) => {
        const ry = variant === 'circle' ? rx : baseRy;

        const { cx, cy } = calculateEllipseCenter(
          P1,
          P2,
          rx,
          ry,
          0,
          0
        );

        let rxLineProps, rxLabelCenter;
        if (variant === 'ellipse') {
          rxLineProps = {
            x1: cx - 0.75,
            y1: cy,
            x2: cx - rx + 0.75,
            y2: cy,
          };
          rxLabelCenter = {
            x: cx - rx / 2,
            y: cy - 1.5,
          };
        } else {
          const deltaX = Math.sin(Math.PI / 4) * rx;
          const deltaY = Math.cos(Math.PI / 4) * ry;
          // Draw a diagonal line (45deg) from the center point to the end point
          rxLineProps = {
            x1: cx + 0.55,
            y1: cy + 0.55,
            x2: cx + deltaX - 0.55,
            y2: cy + deltaY - 0.55,
          };
          rxLabelCenter = {
            x: cx + deltaX / 2 + 1,
            y: cy + deltaY / 2 - 1,
          };
        }

        return (
          <Svg gridGap={2} lineWidth={0.1} viewBox="0 0 32 32">
            <defs>
              <mask id={maskId}>
                <rect width="100%" height="100%" fill="black" />
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={rx - 0.55}
                  ry={ry - 0.55}
                  fill="white"
                />
              </mask>
            </defs>
            <EllipseOutline
              cx={cx}
              cy={cy}
              // HACK: This is a dashed line, and the dashes originate from 0 degrees, the right edge. This means the user can see the break in the dashed line. Let's rotate the ellipse by 90 degreess so the break is at the bottom, covered by the arc. For this to work, we need to flip rx and ry.
              rx={ry}
              ry={rx}
              style={{
                transform: 'rotate(90deg)',
                transformOrigin: `${cx}px ${cy}px`,
              }}
            />
            <CenterPoint cx={cx} cy={cy} r={0.5} />
            <Path
              d={`
                M 0,${P1.y}
                L ${P1.x},${P1.y}
                A ${rx},${ry} 0 0 0 ${P2.x},${P2.y}
                L 32,${P2.y}
              `}
            />
            <Joint r={0.625} cx={P1.x} cy={P1.y} />
            <Joint r={0.625} cx={P2.x} cy={P2.y} />

            <ArcSizeLine
              {...rxLineProps}
              isActive={activeControlId === 'rx'}
              mask={`url(#${maskId})`}
            />
            <BackgroundLabel
              {...rxLabelCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              style={
                variant === 'circle'
                  ? {
                      transform: 'rotate(45deg)',
                      transformOrigin: `${rxLabelCenter.x}px ${rxLabelCenter.y}px`,
                    }
                  : undefined
              }
            >
              {variant === 'circle' ? 'r' : 'rx'}
            </BackgroundLabel>
            <Label
              {...rxLabelCenter}
              data-is-active={activeControlId === 'rx'}
              textAnchor="middle"
              dominantBaseline="middle"
              style={
                variant === 'circle'
                  ? {
                      transform: 'rotate(45deg)',
                      transformOrigin: `${rxLabelCenter.x}px ${rxLabelCenter.y}px`,
                    }
                  : undefined
              }
            >
              {variant === 'circle' ? 'r' : 'rx'}
            </Label>
            {variant === 'ellipse' && (
              <>
                <ArcSizeLine
                  x1={cx}
                  y1={cy + 0.75}
                  x2={cx}
                  y2={cy + ry - 0.75}
                  isActive={activeControlId === 'ry'}
                  mask={`url(#${maskId})`}
                />
                <BackgroundLabel
                  x={cx + 2}
                  y={cy + ry / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  ry
                </BackgroundLabel>
                <Label
                  x={cx + 2}
                  y={cy + ry / 2}
                  data-is-active={activeControlId === 'ry'}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  ry
                </Label>
              </>
            )}
          </Svg>
        );
      }}
    </Demo>
  );
}

const Svg = styled(SvgWithGrid)`
  fill: none;
  width: 100%;
  max-width: 300px;
  margin-inline: auto;
  outline: 1px solid var(--color-gray-300);
  outline-offset: -1px;
  border-radius: 4px;
`;

const EllipseOutline = styled.ellipse`
  fill: none;
  stroke: white;
  stroke-dasharray: 1, 1;
  stroke-width: 0.5px;
`;

const Path = styled.path`
  fill: none;
  stroke: ${ARC_COLOR};
  stroke-width: 1px;
  stroke-linejoin: round;
`;

const Joint = styled.circle`
  fill: white;
  stroke: var(--color-background);
  stroke-width: 0.25px;
`;

const CenterPoint = styled.circle`
  fill: white;
`;

const Value = styled.span`
  font-family: var(--font-family-mono);
`;

const Label = styled.text`
  font-size: 0.1rem;
  fill: var(--color-gray-700);
  font-family: var(--font-family-mono);

  &[data-is-active='true'] {
    fill: var(--wham-primary-500);
  }
`;

const BackgroundLabel = styled(Label)`
  stroke: var(--color-background);
  stroke-width: 0.3px;
`;

export default ArcRadiusDemo;
